<template>
  <div>
    <!-- Hero Section -->
    <section class="relative overflow-hidden py-28 sm:py-36 min-h-screen flex items-center bg-white dark:bg-dark transition-colors duration-300" data-animate="hero" :class="{ 'is-visible': visible.hero }">
      <div class="absolute inset-0 bg-white dark:bg-dark transition-colors duration-300">
        <img v-if="isDark" src="/ciobai_hero_background.webp" alt="" class="absolute inset-0 w-full h-full object-cover" />
        <img v-else src="/ciobai_hero_background_light.webp" alt="" class="absolute inset-0 w-full h-full object-cover opacity-40" />
      </div>

      <div class="hero-particles absolute inset-0 overflow-hidden pointer-events-none">
        <div v-for="i in 8" :key="i" class="particle" :style="particleStyle(i)" />
      </div>

      <div class="container mx-auto px-4 relative z-10">
        <div class="max-w-4xl mx-auto text-center">
          <div class="anim-el d1 inline-flex items-center gap-2 rounded-full bg-white/[0.06] dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.08] px-4 py-1.5 text-[13px] font-medium text-gray-500 dark:text-gray-300 mb-6 backdrop-blur-xl">
            <Bot class="h-4 w-4" />
            {{ $t('aiChatbotSwitzerland.hero.tagline') }}
          </div>

          <h1 class="anim-el d2 text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] text-gray-900 dark:text-white transition-colors duration-300">
            {{ $t('aiChatbotSwitzerland.hero.title') }}
          </h1>

          <p class="anim-el d3 mt-6 text-lg md:text-xl text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300">
            {{ $t('aiChatbotSwitzerland.hero.description') }}
          </p>

          <div class="anim-el d4 mt-10 flex flex-wrap justify-center gap-4">
            <router-link to="/contact" class="group inline-flex items-center h-14 px-8 bg-gray-900 dark:bg-white text-white dark:text-dark rounded-xl font-bold text-lg transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-blue-500/10">
              <MessageSquare class="mr-2 h-5 w-5 transition-transform duration-300 group-hover:rotate-6" />
              {{ $t('aiChatbotSwitzerland.hero.cta') }}
            </router-link>
            <router-link to="/ai-chatbot-development-services-switzerland" class="inline-flex items-center h-14 px-8 border border-gray-200 dark:border-white/[0.08] text-gray-500 dark:text-gray-400 rounded-xl font-bold text-lg transition-colors duration-300">
              <Bot class="mr-2 h-5 w-5" />
              {{ $t('aiChatbotSwitzerland.hero.learnMore') }}
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- What You Need to Know Section -->
    <section class="py-28 bg-white dark:bg-dark transition-colors duration-300" data-animate="whatyouneed" :class="{ 'is-visible': visible.whatyouneed }">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">ESSENTIALS</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">
            {{ $t('chatbot.whatYouNeed.title') }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300">
            {{ $t('chatbot.whatYouNeed.subtitle') }}
          </p>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div class="anim-el d1 space-y-8">
            <div>
              <h3 class="text-2xl font-bold mb-4 text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbot.whatYouNeed.basics.title') }}</h3>
              <div class="space-y-4">
                <div class="rounded-2xl p-6 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 hover:shadow-lg hover:shadow-blue-500/5 hover:-translate-y-0.5 transition-all duration-300">
                  <h4 class="font-semibold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{{ $t('chatbot.whatYouNeed.basics.what.title') }}</h4>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbot.whatYouNeed.basics.what.description') }}</p>
                </div>
                <div class="rounded-2xl p-6 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 hover:shadow-lg hover:shadow-blue-500/5 hover:-translate-y-0.5 transition-all duration-300">
                  <h4 class="font-semibold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{{ $t('chatbot.whatYouNeed.basics.how.title') }}</h4>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbot.whatYouNeed.basics.how.description') }}</p>
                </div>
                <div class="rounded-2xl p-6 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 hover:shadow-lg hover:shadow-blue-500/5 hover:-translate-y-0.5 transition-all duration-300">
                  <h4 class="font-semibold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{{ $t('chatbot.whatYouNeed.basics.when.title') }}</h4>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbot.whatYouNeed.basics.when.description') }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="anim-el d2 space-y-8">
            <div>
              <h3 class="text-2xl font-bold mb-4 text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbot.whatYouNeed.business.title') }}</h3>
              <div class="space-y-4">
                <div class="rounded-2xl p-6 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 hover:shadow-lg hover:shadow-blue-500/5 hover:-translate-y-0.5 transition-all duration-300">
                  <h4 class="font-semibold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{{ $t('chatbot.whatYouNeed.business.immediate.title') }}</h4>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbot.whatYouNeed.business.immediate.description') }}</p>
                </div>
                <div class="rounded-2xl p-6 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 hover:shadow-lg hover:shadow-blue-500/5 hover:-translate-y-0.5 transition-all duration-300">
                  <h4 class="font-semibold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{{ $t('chatbot.whatYouNeed.business.longterm.title') }}</h4>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbot.whatYouNeed.business.longterm.description') }}</p>
                </div>
                <div class="rounded-2xl p-6 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 hover:shadow-lg hover:shadow-blue-500/5 hover:-translate-y-0.5 transition-all duration-300">
                  <h4 class="font-semibold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{{ $t('chatbot.whatYouNeed.business.roi.title') }}</h4>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbot.whatYouNeed.business.roi.description') }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-12 text-center anim-el d3">
          <div class="max-w-4xl mx-auto rounded-2xl p-8 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] transition-colors duration-300">
            <h3 class="text-2xl font-bold mb-6 text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbot.whatYouNeed.summary.title') }}</h3>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div v-for="point in summaryPoints" :key="point.title" class="text-center">
                <div class="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] mx-auto mb-4 transition-colors duration-300">
                  <component :is="point.icon" class="h-5 w-5 text-gray-500 dark:text-gray-400" />
                </div>
                <h4 class="font-semibold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{{ point.title }}</h4>
                <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ point.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Switzerland Connection Section -->
    <section class="py-28 bg-gray-50 dark:bg-dark transition-colors duration-300" data-animate="switzerland" :class="{ 'is-visible': visible.switzerland }">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">SWITZERLAND</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">
            {{ $t('aiChatbotSwitzerland.switzerlandSpecific.title') }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300">
            {{ $t('aiChatbotSwitzerland.switzerlandSpecific.subtitle') }}
          </p>
        </div>

        <div class="anim-el d1 max-w-4xl mx-auto rounded-2xl p-8 bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 hover:shadow-lg hover:shadow-blue-500/5 transition-all duration-300">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 class="text-xl font-semibold mb-4 text-gray-900 dark:text-white transition-colors duration-300">{{ $t('aiChatbotSwitzerland.switzerlandSpecific.multilingual.title') }}</h3>
              <p class="text-gray-500 dark:text-gray-400 mb-4 transition-colors duration-300">{{ $t('aiChatbotSwitzerland.switzerlandSpecific.multilingual.description') }}</p>
              <router-link to="/ai-chatbot-development-services-switzerland" class="inline-flex items-center text-gray-900 dark:text-white font-semibold transition-colors duration-300">
                {{ $t('aiChatbotSwitzerland.switzerlandSpecific.exploreDevelopment') }}
                <Globe class="ml-2 h-4 w-4" />
              </router-link>
            </div>
            <div class="text-center">
              <div class="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] transition-colors duration-300">
                <Globe class="h-10 w-10 text-gray-500 dark:text-gray-400" />
              </div>
              <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('aiChatbotSwitzerland.switzerlandSpecific.compliance.description') }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- What is a Chatbot Section -->
    <section class="py-28 bg-white dark:bg-dark transition-colors duration-300" data-animate="whatischatbot" :class="{ 'is-visible': visible.whatischatbot }">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">UNDERSTANDING CHATBOTS</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">
            {{ $t('chatbot.whatIsChatbot.title') }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300">
            {{ $t('chatbot.whatIsChatbot.subtitle') }}
          </p>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div class="anim-el d1">
            <h3 class="text-2xl font-bold mb-6 text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbot.whatIsChatbot.problems.title') }}</h3>
            <div class="space-y-4">
              <div class="flex items-start space-x-3">
                <div class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1 bg-red-100 dark:bg-red-500/10 transition-colors duration-300">
                  <span class="text-red-600 dark:text-red-400 text-sm font-bold">×</span>
                </div>
                <div>
                  <p class="text-gray-900 dark:text-white font-medium transition-colors duration-300">{{ $t('chatbot.whatIsChatbot.problems.waiting.title') }}</p>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbot.whatIsChatbot.problems.waiting.description') }}</p>
                </div>
              </div>
              <div class="flex items-start space-x-3">
                <div class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1 bg-red-100 dark:bg-red-500/10 transition-colors duration-300">
                  <span class="text-red-600 dark:text-red-400 text-sm font-bold">×</span>
                </div>
                <div>
                  <p class="text-gray-900 dark:text-white font-medium transition-colors duration-300">{{ $t('chatbot.whatIsChatbot.problems.language.title') }}</p>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbot.whatIsChatbot.problems.language.description') }}</p>
                </div>
              </div>
              <div class="flex items-start space-x-3">
                <div class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1 bg-red-100 dark:bg-red-500/10 transition-colors duration-300">
                  <span class="text-red-600 dark:text-red-400 text-sm font-bold">×</span>
                </div>
                <div>
                  <p class="text-gray-900 dark:text-white font-medium transition-colors duration-300">{{ $t('chatbot.whatIsChatbot.problems.costs.title') }}</p>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbot.whatIsChatbot.problems.costs.description') }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="anim-el d2">
            <h3 class="text-2xl font-bold mb-6 text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbot.whatIsChatbot.solutions.title') }}</h3>
            <div class="space-y-4">
              <div class="flex items-start space-x-3">
                <div class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1 bg-gray-100 dark:bg-white/[0.06] transition-colors duration-300">
                  <Check class="h-3 w-3 text-gray-500 dark:text-gray-400" />
                </div>
                <div>
                  <p class="text-gray-900 dark:text-white font-medium transition-colors duration-300">{{ $t('chatbot.whatIsChatbot.solutions.immediate.title') }}</p>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbot.whatIsChatbot.solutions.immediate.description') }}</p>
                </div>
              </div>
              <div class="flex items-start space-x-3">
                <div class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1 bg-gray-100 dark:bg-white/[0.06] transition-colors duration-300">
                  <Check class="h-3 w-3 text-gray-500 dark:text-gray-400" />
                </div>
                <div>
                  <p class="text-gray-900 dark:text-white font-medium transition-colors duration-300">{{ $t('chatbot.whatIsChatbot.solutions.multilingual.title') }}</p>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbot.whatIsChatbot.solutions.multilingual.description') }}</p>
                </div>
              </div>
              <div class="flex items-start space-x-3">
                <div class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1 bg-gray-100 dark:bg-white/[0.06] transition-colors duration-300">
                  <Check class="h-3 w-3 text-gray-500 dark:text-gray-400" />
                </div>
                <div>
                  <p class="text-gray-900 dark:text-white font-medium transition-colors duration-300">{{ $t('chatbot.whatIsChatbot.solutions.costEffective.title') }}</p>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbot.whatIsChatbot.solutions.costEffective.description') }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- How Chatbots Work Section -->
    <section class="py-28 bg-gray-50 dark:bg-dark transition-colors duration-300" data-animate="howitworks" :class="{ 'is-visible': visible.howitworks }">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">HOW IT WORKS</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">
            {{ $t('chatbot.howItWorks.title') }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300">
            {{ $t('chatbot.howItWorks.subtitle') }}
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div v-for="(step, index) in howItWorksSteps" :key="step.title" class="group anim-el text-center" :class="'d' + (index + 1)">
            <div class="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] mx-auto mb-6 transition-all duration-300 group-hover:bg-blue-50 dark:group-hover:bg-blue-500/10">
              <component :is="step.icon" class="h-5 w-5 text-gray-500 dark:text-gray-400 transition-colors duration-300 group-hover:text-blue-500" />
            </div>
            <h3 class="text-lg font-semibold mb-2 text-gray-900 dark:text-white transition-colors duration-300">{{ step.title }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ step.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="py-28 bg-white dark:bg-dark transition-colors duration-300" data-animate="features" :class="{ 'is-visible': visible.features }">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">FEATURES</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">
            {{ $t('chatbot.features.title') }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300">
            {{ $t('chatbot.features.subtitle') }}
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div v-for="(feature, index) in features" :key="feature.title" class="group anim-el rounded-2xl p-6 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 hover:shadow-lg hover:shadow-blue-500/5 hover:-translate-y-0.5 transition-all duration-300" :class="'d' + (index + 1)">
            <div class="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] mb-6 transition-all duration-300 group-hover:bg-blue-50 dark:group-hover:bg-blue-500/10">
              <component :is="feature.icon" class="h-5 w-5 text-gray-500 dark:text-gray-400 transition-colors duration-300 group-hover:text-blue-500" />
            </div>
            <h3 class="text-xl font-semibold mb-4 text-gray-900 dark:text-white transition-colors duration-300">{{ feature.title }}</h3>
            <p class="text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ feature.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Use Cases Section -->
    <section class="py-28 bg-gray-50 dark:bg-dark transition-colors duration-300" data-animate="usecases" :class="{ 'is-visible': visible.usecases }">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">USE CASES</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">
            {{ $t('chatbot.useCases.title') }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300">
            {{ $t('chatbot.useCases.subtitle') }}
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div v-for="(useCase, index) in useCases" :key="useCase.title" class="group anim-el text-center" :class="'d' + (index + 1)">
            <div class="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] mx-auto mb-4 transition-all duration-300 group-hover:bg-blue-50 dark:group-hover:bg-blue-500/10">
              <component :is="useCase.icon" class="h-5 w-5 text-gray-500 dark:text-gray-400 transition-colors duration-300 group-hover:text-blue-500" />
            </div>
            <h3 class="text-lg font-semibold mb-2 text-gray-900 dark:text-white transition-colors duration-300">{{ useCase.title }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ useCase.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Industry Applications Section -->
    <section class="py-28 bg-white dark:bg-dark transition-colors duration-300" data-animate="industries" :class="{ 'is-visible': visible.industries }">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">INDUSTRIES</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">
            {{ $t('chatbot.industries.title') }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300">
            {{ $t('chatbot.industries.subtitle') }}
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div v-for="(industry, index) in industries" :key="industry.title" class="group anim-el rounded-2xl p-6 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 hover:shadow-lg hover:shadow-blue-500/5 hover:-translate-y-0.5 transition-all duration-300" :class="'d' + (index + 1)">
            <div class="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] mb-4 transition-all duration-300 group-hover:bg-blue-50 dark:group-hover:bg-blue-500/10">
              <component :is="industry.icon" class="h-5 w-5 text-gray-500 dark:text-gray-400 transition-colors duration-300 group-hover:text-blue-500" />
            </div>
            <h3 class="text-lg font-semibold mb-3 text-gray-900 dark:text-white transition-colors duration-300">{{ industry.title }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 mb-4 transition-colors duration-300">{{ industry.description }}</p>
            <ul class="text-xs text-gray-500 dark:text-gray-400 space-y-1">
              <li v-for="application in industry.applications" :key="application" class="flex items-center">
                <Check class="h-3 w-3 text-blue-400 mr-2 flex-shrink-0" />
                {{ application }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Benefits Section -->
    <section class="py-28 bg-gray-50 dark:bg-dark transition-colors duration-300" data-animate="benefits" :class="{ 'is-visible': visible.benefits }">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">BENEFITS</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">
            {{ $t('chatbot.benefits.title') }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300">
            {{ $t('chatbot.benefits.subtitle') }}
          </p>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div class="space-y-6">
            <div v-for="(benefit, index) in benefits" :key="benefit.title" class="group anim-el flex items-start space-x-4" :class="'d' + (index + 1)">
              <div class="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] flex-shrink-0 transition-all duration-300 group-hover:bg-blue-50 dark:group-hover:bg-blue-500/10">
                <component :is="benefit.icon" class="h-5 w-5 text-gray-500 dark:text-gray-400 transition-colors duration-300 group-hover:text-blue-500" />
              </div>
              <div>
                <h3 class="text-lg font-semibold mb-2 text-gray-900 dark:text-white transition-colors duration-300">{{ benefit.title }}</h3>
                <p class="text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ benefit.description }}</p>
              </div>
            </div>
          </div>

          <div class="anim-el d5">
            <div class="rounded-2xl p-8 bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] transition-colors duration-300">
              <div class="text-center">
                <div class="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] mx-auto mb-6 transition-colors duration-300">
                  <TrendingUp class="h-5 w-5 text-gray-500 dark:text-gray-400" />
                </div>
                <h3 class="text-2xl font-bold mb-4 text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbot.benefits.stats.title') }}</h3>
                <div class="grid grid-cols-2 gap-6">
                  <div class="text-center">
                    <div class="text-3xl font-bold mb-2 hero-gradient-text">{{ $t('chatbot.benefits.stats.availabilityValue') }}</div>
                    <div class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbot.benefits.stats.availability') }}</div>
                  </div>
                  <div class="text-center">
                    <div class="text-3xl font-bold mb-2 hero-gradient-text">{{ $t('chatbot.benefits.stats.efficiencyValue') }}</div>
                    <div class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbot.benefits.stats.efficiency') }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Development Process Section -->
    <section class="py-28 bg-white dark:bg-dark transition-colors duration-300" data-animate="development" :class="{ 'is-visible': visible.development }">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">PROCESS</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">
            {{ $t('chatbot.development.title') }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300">
            {{ $t('chatbot.development.subtitle') }}
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div v-for="(step, index) in developmentSteps" :key="index" class="anim-el text-center" :class="'d' + (index + 1)">
            <div class="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-6 bg-gray-900 dark:bg-white text-white dark:text-dark font-bold text-lg transition-colors duration-300">
              {{ index + 1 }}
            </div>
            <h3 class="text-xl font-semibold mb-4 text-gray-900 dark:text-white transition-colors duration-300">{{ step.title }}</h3>
            <p class="text-gray-500 dark:text-gray-400 mb-4 transition-colors duration-300">{{ step.description }}</p>
            <ul class="text-sm text-gray-500 dark:text-gray-400 space-y-2 text-left">
              <li v-for="detail in step.details" :key="detail" class="flex items-center">
                <Check class="h-4 w-4 text-blue-400 mr-2 flex-shrink-0" />
                {{ detail }}
              </li>
            </ul>
          </div>
        </div>

        <div class="mt-16 text-center anim-el d5">
          <h3 class="text-2xl font-bold mb-8 text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbot.development.timeline.title') }}</h3>
          <div class="max-w-4xl mx-auto rounded-2xl p-8 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] transition-colors duration-300">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div v-for="phase in timelinePhases" :key="phase.title" class="text-center">
                <div class="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] mx-auto mb-4 transition-colors duration-300">
                  <component :is="phase.icon" class="h-5 w-5 text-gray-500 dark:text-gray-400" />
                </div>
                <h4 class="font-semibold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{{ phase.title }}</h4>
                <p class="text-sm text-gray-500 dark:text-gray-400 mb-2 transition-colors duration-300">{{ phase.duration }}</p>
                <p class="text-xs text-gray-400 dark:text-gray-500 transition-colors duration-300">{{ phase.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Technology & Integration Section -->
    <section class="py-28 bg-gray-50 dark:bg-dark transition-colors duration-300" data-animate="technology" :class="{ 'is-visible': visible.technology }">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">TECHNOLOGY</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">
            {{ $t('chatbot.technology.title') }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300">
            {{ $t('chatbot.technology.subtitle') }}
          </p>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div class="anim-el d1">
            <h3 class="text-2xl font-bold mb-6 text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbot.technology.ai.title') }}</h3>
            <div class="space-y-4">
              <div class="flex items-start space-x-3">
                <div class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1 bg-gray-100 dark:bg-white/[0.06] transition-colors duration-300">
                  <Check class="h-3 w-3 text-gray-500 dark:text-gray-400" />
                </div>
                <div>
                  <p class="text-gray-900 dark:text-white font-medium transition-colors duration-300">{{ $t('chatbot.technology.ai.nlp.title') }}</p>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbot.technology.ai.nlp.description') }}</p>
                </div>
              </div>
              <div class="flex items-start space-x-3">
                <div class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1 bg-gray-100 dark:bg-white/[0.06] transition-colors duration-300">
                  <Check class="h-3 w-3 text-gray-500 dark:text-gray-400" />
                </div>
                <div>
                  <p class="text-gray-900 dark:text-white font-medium transition-colors duration-300">{{ $t('chatbot.technology.ai.ml.title') }}</p>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbot.technology.ai.ml.description') }}</p>
                </div>
              </div>
              <div class="flex items-start space-x-3">
                <div class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1 bg-gray-100 dark:bg-white/[0.06] transition-colors duration-300">
                  <Check class="h-3 w-3 text-gray-500 dark:text-gray-400" />
                </div>
                <div>
                  <p class="text-gray-900 dark:text-white font-medium transition-colors duration-300">{{ $t('chatbot.technology.ai.context.title') }}</p>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbot.technology.ai.context.description') }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="anim-el d2">
            <h3 class="text-2xl font-bold mb-6 text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbot.technology.integration.title') }}</h3>
            <div class="grid grid-cols-2 gap-4">
              <div v-for="platform in integrationPlatforms" :key="platform.name" class="group rounded-2xl p-4 text-center bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 hover:shadow-lg hover:shadow-blue-500/5 hover:-translate-y-0.5 transition-all duration-300">
                <component :is="platform.icon" class="h-6 w-6 text-gray-500 dark:text-gray-400 mx-auto mb-2 transition-colors duration-300 group-hover:text-blue-500" />
                <p class="text-sm font-medium text-gray-900 dark:text-white transition-colors duration-300">{{ platform.name }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="py-28 bg-white dark:bg-dark transition-colors duration-300" data-animate="faq" :class="{ 'is-visible': visible.faq }">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">FAQ</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">
            {{ $t('chatbot.faq.title') }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300">
            {{ $t('chatbot.faq.subtitle') }}
          </p>
        </div>

        <div class="anim-el d1 max-w-3xl mx-auto divide-y divide-gray-200 dark:divide-white/[0.06]">
          <div v-for="(faq, index) in faqs" :key="index">
            <button type="button" @click="toggleFaq(index)" class="group w-full flex justify-between items-center py-5 text-left">
              <h3 class="text-[15px] font-semibold text-gray-900 dark:text-white pr-8 transition-colors duration-300 group-hover:text-blue-500">{{ faq.question }}</h3>
              <ChevronDown class="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0 transition-all duration-300" :class="{ 'rotate-180': activeFaqIndex === index }" />
            </button>
            <div v-show="activeFaqIndex === index" class="pb-5 -mt-1">
              <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">{{ faq.answer }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Technology Stack Section -->
    <section class="py-28 bg-gray-50 dark:bg-dark transition-colors duration-300" data-animate="techstack" :class="{ 'is-visible': visible.techstack }">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">TECH STACK</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">
            {{ $t('chatbot.techStack.title') }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300">
            {{ $t('chatbot.techStack.subtitle') }}
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div v-for="(tech, index) in techStack" :key="tech.name" class="group anim-el rounded-2xl p-6 bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 hover:shadow-lg hover:shadow-blue-500/5 hover:-translate-y-0.5 transition-all duration-300" :class="'d' + (index + 1)">
            <div class="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] mx-auto mb-6 transition-all duration-300 group-hover:bg-blue-50 dark:group-hover:bg-blue-500/10">
              <component :is="tech.icon" class="h-5 w-5 text-gray-500 dark:text-gray-400 transition-colors duration-300 group-hover:text-blue-500" />
            </div>
            <h3 class="text-xl font-semibold mb-4 text-gray-900 dark:text-white text-center transition-colors duration-300">{{ tech.name }}</h3>
            <p class="text-gray-500 dark:text-gray-400 mb-4 text-center transition-colors duration-300">{{ tech.description }}</p>
            <ul class="text-sm text-gray-500 dark:text-gray-400 space-y-2">
              <li v-for="feature in tech.features" :key="feature" class="flex items-center">
                <Check class="h-4 w-4 text-blue-400 mr-2 flex-shrink-0" />
                {{ feature }}
              </li>
            </ul>
          </div>
        </div>

        <div class="mt-12 text-center anim-el d4">
          <p class="text-lg text-gray-500 dark:text-gray-400 mb-6 transition-colors duration-300">{{ $t('chatbot.techStack.whyChoose.title') }}</p>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div v-for="reason in whyChooseTech" :key="reason.title" class="text-center">
              <div class="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] mx-auto mb-4 transition-colors duration-300">
                <component :is="reason.icon" class="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </div>
              <h4 class="font-semibold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{{ reason.title }}</h4>
              <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ reason.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="relative py-28 md:py-40 overflow-hidden">
      <div class="absolute inset-0 bg-gray-950">
        <img src="/ciobai_hero_background.webp" alt="" class="absolute inset-0 w-full h-full object-cover opacity-60" />
      </div>

      <div class="hero-particles absolute inset-0 overflow-hidden pointer-events-none">
        <div v-for="i in 8" :key="'cta-' + i" class="particle" :style="particleStyle(i)" />
      </div>

      <div class="container mx-auto px-4 text-center relative z-10">
        <div class="max-w-5xl mx-auto">
          <h2 class="text-4xl md:text-5xl font-extrabold text-white mb-4">
            {{ $t('aiChatbotSwitzerland.cta.title') }}
          </h2>
          <p class="text-xl md:text-2xl mb-8 text-white/90 font-semibold max-w-3xl mx-auto">
            {{ $t('aiChatbotSwitzerland.cta.description') }}
          </p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <router-link to="/contact" class="group inline-flex items-center h-14 px-8 bg-white text-gray-900 font-bold text-lg rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-blue-500/10">
              <MessageSquare class="mr-2 h-5 w-5 transition-transform duration-300 group-hover:rotate-6" />
              {{ $t('aiChatbotSwitzerland.cta.button') }}
            </router-link>
            <router-link to="/ai-chatbot-development-services-switzerland" class="inline-flex items-center h-14 px-8 border border-white/20 text-white rounded-xl font-semibold text-lg">
              {{ $t('aiChatbotSwitzerland.switzerlandSpecific.exploreDevelopment') }}
            </router-link>
          </div>
          <div class="flex flex-wrap justify-center gap-6 text-sm text-white/90">
            <div class="flex items-center"><Check class="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" />Swiss-Based</div>
            <div class="flex items-center"><Check class="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" />GDPR Compliant</div>
            <div class="flex items-center"><Check class="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" />Multilingual Support</div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted } from 'vue'
import {
  MessageSquare,
  Bot,
  Clock,
  Globe,
  DollarSign,
  Zap,
  Users,
  Shield,
  BarChart3,
  HeadphonesIcon,
  ShoppingCart,
  BookOpen,
  Presentation,
  TrendingUp,
  Check,
  Brain,
  Database,
  Smartphone,
  Building,
  Car,
  Heart,
  GraduationCap,
  CreditCard,
  Settings,
  Webhook,
  MessageCircle,
  Instagram,
  Facebook,
  Twitter,
  Code,
  ChevronDown
} from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import { useTheme } from '../composables/useTheme'
const { t } = useI18n()
const { isDark } = useTheme()

const visible = reactive({})
const activeFaqIndex = ref(null)
let observer = null

function toggleFaq(index) {
  activeFaqIndex.value = activeFaqIndex.value === index ? null : index
}

function particleStyle(i) {
  const left = ((i * 17 + 5) % 90) + 5
  const dur = 14 + (i % 4) * 2
  const delay = (i * 1.3) % 6
  const size = 2 + (i % 3)
  return {
    position: 'absolute',
    bottom: '-10px',
    left: left + '%',
    width: size + 'px',
    height: size + 'px',
    borderRadius: '50%',
    background: 'rgba(59, 130, 246, 0.5)',
    animation: `particleRise ${dur}s linear ${delay}s infinite`
  }
}

onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) visible[entry.target.dataset.animate] = true
    })
  }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' })
  document.querySelectorAll('[data-animate]').forEach(el => observer.observe(el))
})

onUnmounted(() => { if (observer) observer.disconnect() })

const techStack = [
  {
    icon: Bot,
    name: t('chatbot.techStack.n8n.name'),
    description: t('chatbot.techStack.n8n.description'),
    features: [
      t('chatbot.techStack.n8n.features.visual'),
      t('chatbot.techStack.n8n.features.integrations'),
      t('chatbot.techStack.n8n.features.webhooks'),
      t('chatbot.techStack.n8n.features.errorHandling')
    ]
  },
  {
    icon: Brain,
    name: t('chatbot.techStack.botpress.name'),
    description: t('chatbot.techStack.botpress.description'),
    features: [
      t('chatbot.techStack.botpress.features.nlp'),
      t('chatbot.techStack.botpress.features.multichannel'),
      t('chatbot.techStack.botpress.features.analytics'),
      t('chatbot.techStack.botpress.features.modules')
    ]
  },
  {
    icon: Code,
    name: t('chatbot.techStack.python.name'),
    description: t('chatbot.techStack.python.description'),
    features: [
      t('chatbot.techStack.python.features.langchain'),
      t('chatbot.techStack.python.features.models'),
      t('chatbot.techStack.python.features.architecture'),
      t('chatbot.techStack.python.features.flexibility')
    ]
  }
]

const whyChooseTech = [
  {
    icon: Zap,
    title: t('chatbot.techStack.whyChoose.flexibility.title'),
    description: t('chatbot.techStack.whyChoose.flexibility.description')
  },
  {
    icon: Shield,
    title: t('chatbot.techStack.whyChoose.reliability.title'),
    description: t('chatbot.techStack.whyChoose.reliability.description')
  },
  {
    icon: TrendingUp,
    title: t('chatbot.techStack.whyChoose.scalability.title'),
    description: t('chatbot.techStack.whyChoose.scalability.description')
  }
]

const developmentSteps = [
  {
    title: t('chatbot.development.step1.title'),
    description: t('chatbot.development.step1.description'),
    details: [
      t('chatbot.development.step1.detail1'),
      t('chatbot.development.step1.detail2'),
      t('chatbot.development.step1.detail3')
    ]
  },
  {
    title: t('chatbot.development.step2.title'),
    description: t('chatbot.development.step2.description'),
    details: [
      t('chatbot.development.step2.detail1'),
      t('chatbot.development.step2.detail2'),
      t('chatbot.development.step2.detail3')
    ]
  },
  {
    title: t('chatbot.development.step3.title'),
    description: t('chatbot.development.step3.description'),
    details: [
      t('chatbot.development.step3.detail1'),
      t('chatbot.development.step3.detail2'),
      t('chatbot.development.step3.detail3')
    ]
  },
  {
    title: t('chatbot.development.step4.title'),
    description: t('chatbot.development.step4.description'),
    details: [
      t('chatbot.development.step4.detail1'),
      t('chatbot.development.step4.detail2'),
      t('chatbot.development.step4.detail3')
    ]
  }
]

const timelinePhases = [
  {
    icon: Clock,
    title: t('chatbot.development.timeline.phase1.title'),
    duration: t('chatbot.development.timeline.phase1.duration'),
    description: t('chatbot.development.timeline.phase1.description')
  },
  {
    icon: Zap,
    title: t('chatbot.development.timeline.phase2.title'),
    duration: t('chatbot.development.timeline.phase2.duration'),
    description: t('chatbot.development.timeline.phase2.description')
  },
  {
    icon: Check,
    title: t('chatbot.development.timeline.phase3.title'),
    duration: t('chatbot.development.timeline.phase3.duration'),
    description: t('chatbot.development.timeline.phase3.description')
  }
]

const summaryPoints = [
  {
    icon: Users,
    title: t('chatbot.whatYouNeed.summary.point1.title'),
    description: t('chatbot.whatYouNeed.summary.point1.description')
  },
  {
    icon: TrendingUp,
    title: t('chatbot.whatYouNeed.summary.point2.title'),
    description: t('chatbot.whatYouNeed.summary.point2.description')
  },
  {
    icon: Shield,
    title: t('chatbot.whatYouNeed.summary.point3.title'),
    description: t('chatbot.whatYouNeed.summary.point3.description')
  }
]

const howItWorksSteps = [
  {
    icon: MessageCircle,
    title: t('chatbot.howItWorks.step1.title'),
    description: t('chatbot.howItWorks.step1.description')
  },
  {
    icon: Brain,
    title: t('chatbot.howItWorks.step2.title'),
    description: t('chatbot.howItWorks.step2.description')
  },
  {
    icon: Database,
    title: t('chatbot.howItWorks.step3.title'),
    description: t('chatbot.howItWorks.step3.description')
  },
  {
    icon: MessageSquare,
    title: t('chatbot.howItWorks.step4.title'),
    description: t('chatbot.howItWorks.step4.description')
  }
]

const features = [
  {
    icon: Bot,
    title: t('chatbot.features.ai.title'),
    description: t('chatbot.features.ai.description')
  },
  {
    icon: Clock,
    title: t('chatbot.features.availability.title'),
    description: t('chatbot.features.availability.description')
  },
  {
    icon: Globe,
    title: t('chatbot.features.multilingual.title'),
    description: t('chatbot.features.multilingual.description')
  },
  {
    icon: Zap,
    title: t('chatbot.features.integration.title'),
    description: t('chatbot.features.integration.description')
  },
  {
    icon: Users,
    title: t('chatbot.features.scalability.title'),
    description: t('chatbot.features.scalability.description')
  },
  {
    icon: Shield,
    title: t('chatbot.features.security.title'),
    description: t('chatbot.features.security.description')
  }
]

const useCases = [
  {
    icon: HeadphonesIcon,
    title: t('chatbot.useCases.customerService.title'),
    description: t('chatbot.useCases.customerService.description')
  },
  {
    icon: ShoppingCart,
    title: t('chatbot.useCases.sales.title'),
    description: t('chatbot.useCases.sales.description')
  },
  {
    icon: BookOpen,
    title: t('chatbot.useCases.support.title'),
    description: t('chatbot.useCases.support.description')
  },
  {
    icon: Presentation,
    title: t('chatbot.useCases.training.title'),
    description: t('chatbot.useCases.training.description')
  }
]

const industries = [
  {
    icon: Building,
    title: t('chatbot.industries.ecommerce.title'),
    description: t('chatbot.industries.ecommerce.description'),
    applications: [
      t('chatbot.industries.ecommerce.app1'),
      t('chatbot.industries.ecommerce.app2'),
      t('chatbot.industries.ecommerce.app3')
    ]
  },
  {
    icon: Heart,
    title: t('chatbot.industries.healthcare.title'),
    description: t('chatbot.industries.healthcare.description'),
    applications: [
      t('chatbot.industries.healthcare.app1'),
      t('chatbot.industries.healthcare.app2'),
      t('chatbot.industries.healthcare.app3')
    ]
  },
  {
    icon: GraduationCap,
    title: t('chatbot.industries.education.title'),
    description: t('chatbot.industries.education.description'),
    applications: [
      t('chatbot.industries.education.app1'),
      t('chatbot.industries.education.app2'),
      t('chatbot.industries.education.app3')
    ]
  },
  {
    icon: Car,
    title: t('chatbot.industries.travel.title'),
    description: t('chatbot.industries.travel.description'),
    applications: [
      t('chatbot.industries.travel.app1'),
      t('chatbot.industries.travel.app2'),
      t('chatbot.industries.travel.app3')
    ]
  },
  {
    icon: CreditCard,
    title: t('chatbot.industries.finance.title'),
    description: t('chatbot.industries.finance.description'),
    applications: [
      t('chatbot.industries.finance.app1'),
      t('chatbot.industries.finance.app2'),
      t('chatbot.industries.finance.app3')
    ]
  },
  {
    icon: Settings,
    title: t('chatbot.industries.manufacturing.title'),
    description: t('chatbot.industries.manufacturing.description'),
    applications: [
      t('chatbot.industries.manufacturing.app1'),
      t('chatbot.industries.manufacturing.app2'),
      t('chatbot.industries.manufacturing.app3')
    ]
  }
]

const benefits = [
  {
    icon: Clock,
    title: t('chatbot.benefits.time.title'),
    description: t('chatbot.benefits.time.description')
  },
  {
    icon: DollarSign,
    title: t('chatbot.benefits.cost.title'),
    description: t('chatbot.benefits.cost.description')
  },
  {
    icon: Users,
    title: t('chatbot.benefits.satisfaction.title'),
    description: t('chatbot.benefits.satisfaction.description')
  },
  {
    icon: BarChart3,
    title: t('chatbot.benefits.analytics.title'),
    description: t('chatbot.benefits.analytics.description')
  }
]

const integrationPlatforms = [
  { icon: Webhook, name: t('chatbot.techStack.platforms.api') },
  { icon: MessageCircle, name: t('chatbot.techStack.platforms.whatsapp') },
  { icon: Facebook, name: t('chatbot.techStack.platforms.facebook') },
  { icon: Instagram, name: t('chatbot.techStack.platforms.instagram') },
  { icon: Twitter, name: t('chatbot.techStack.platforms.twitter') },
  { icon: Smartphone, name: t('chatbot.techStack.platforms.mobile') }
]

const faqs = [
  {
    question: t('chatbot.faq.q1.question'),
    answer: t('chatbot.faq.q1.answer')
  },
  {
    question: t('chatbot.faq.q2.question'),
    answer: t('chatbot.faq.q2.answer')
  },
  {
    question: t('chatbot.faq.q3.question'),
    answer: t('chatbot.faq.q3.answer')
  },
  {
    question: t('chatbot.faq.q4.question'),
    answer: t('chatbot.faq.q4.answer')
  },
  {
    question: t('chatbot.faq.q5.question'),
    answer: t('chatbot.faq.q5.answer')
  },
  {
    question: t('chatbot.faq.q6.question'),
    answer: t('chatbot.faq.q6.answer')
  }
]

defineOptions({
  name: 'AiChatbotSwitzerlandPage'
})
</script>

<style scoped>
.hero-gradient-text {
  background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #2563eb 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.anim-el {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.7s cubic-bezier(.22,1,.36,1), transform 0.7s cubic-bezier(.22,1,.36,1);
}
.is-visible .anim-el { opacity: 1; transform: translateY(0); }
.d1 { transition-delay: 0.05s; }
.d2 { transition-delay: 0.15s; }
.d3 { transition-delay: 0.25s; }
.d4 { transition-delay: 0.35s; }
.d5 { transition-delay: 0.45s; }
.d6 { transition-delay: 0.55s; }
.d7 { transition-delay: 0.65s; }
.d8 { transition-delay: 0.75s; }
.d9 { transition-delay: 0.85s; }

@keyframes particleRise {
  0% { transform: translateY(0) translateX(0); opacity: 0; }
  5% { opacity: 0.7; }
  50% { transform: translateY(-50vh) translateX(20px); opacity: 0.3; }
  100% { transform: translateY(-100vh) translateX(-10px); opacity: 0; }
}
</style>
